import { useTheme } from 'canoe';
import moment from 'moment';
import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { TimePeriod } from '@candu-ai/analytics/types';

import { GraphEmbed } from '../GraphEmbed';
import { CustomTooltip } from '../Tooltip';
import { getMomentPeriodFormat } from '../getMomentPeriodFormat';
import { getSeriesColor } from '../getSeriesColor';

export interface TimeChartProps {
  entries: { label: string; value: string }[];
  chartData: any[];
  title?: string;
  executedAt?: number;
  subtitle?: string | JSX.Element;
  legend: string;
  syncId?: string;
  tooltipText?: string;
  unit?: string;
  showXAxis?: boolean;
  period?: TimePeriod;
  onDownloadData?: () => Promise<void>;
  onRefreshQuery?: () => Promise<void>;
  xAxisDataKey: string;
  isMultiSeries?: boolean;
  isLoading?: boolean;
}

/**
 *
 * Returns a Chart with as many lines as entries
 * The xAxis is calculated after the metadata of the first entry
 * for now only supports ti/uti but we should upgrade to ts/uts
 */
export const TimeChart = ({
  title,
  subtitle,
  executedAt,
  entries,
  chartData,
  legend,
  tooltipText,
  unit,
  syncId,
  onDownloadData,
  onRefreshQuery,
  xAxisDataKey,
  showXAxis = true,
  isMultiSeries = false,
  period = TimePeriod.Days,
  isLoading,
}: TimeChartProps) => {
  const theme = useTheme();

  const allValues = chartData.reduce((prev, curr) => {
    const entryValues = entries.map((entry) => curr[entry.value]);
    return [...prev, ...entryValues];
  }, []);

  const maxValue = parseInt(`${Math.max(...allValues)}`, 10);
  const minValue = parseInt(`${Math.min(...allValues)}`, 10);

  return (
    <GraphEmbed
      title={title}
      executedAt={executedAt}
      subtitle={subtitle}
      tooltipText={tooltipText}
      onDownloadData={onDownloadData}
      onRefreshQuery={onRefreshQuery}
      isLoading={isLoading}
    >
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} syncId={syncId}>
          <CartesianGrid strokeDasharray="3 3" />
          {showXAxis && (
            <XAxis
              dataKey={xAxisDataKey ?? ''}
              tickFormatter={(value: Date) => moment(value).format(getMomentPeriodFormat(period))}
            />
          )}
          <Tooltip
            content={CustomTooltip(
              legend,
              isMultiSeries,
              getMomentPeriodFormat(period) as any,
              unit,
            )}
          />
          <YAxis
            domain={[minValue, maxValue]}
            unit={unit}
            allowDecimals={false}
            tickFormatter={(value: number) => value.toLocaleString('en-GB')}
          />
          {entries.map(({ value, label }, idx) => (
            <Line
              key={`${value}${label}`}
              type="monotone"
              name={label}
              dataKey={value}
              stroke={theme.colors[getSeriesColor(idx)]}
              dot={() => null}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </GraphEmbed>
  );
};
